"use client";

import type { MediaVideo as GeneratedMediaVideoProps } from "@homeinstead/schemas";

import { cn } from "@homeinstead/ui";
import {
  getMediaAspectClass,
  MediaFigure,
} from "@homeinstead/ui/components/Media";
import { VideoPlayer } from "@homeinstead/ui/components/VideoPlayer";
import { useMemo } from "react";
import { usePageBuilderCookieConsent } from "~www/components/PageBuilder/page-builder-runtime-context";
import { getSanityFileUrl } from "~www/lib/sanity/get-sanity-file-url";
import { urlFor } from "~www/lib/sanity/sanity-images";
import { buildVideoObjectFromMediaVideo } from "~www/lib/videos/build-video-object-from-block";
import { prepareVideoURL } from "~www/lib/videos/video-utils";
import { VideoSchema } from "../VideoSchema";

type MediaVideoProps = Omit<GeneratedMediaVideoProps, "_type"> & {
  _key?: string;
  className?: string;
};

function buildHostedVideoUrl(sourceType: string, videoId: string) {
  return sourceType === "vimeo"
    ? `https://vimeo.com/${videoId}`
    : `https://www.youtube.com/watch?v=${videoId}`;
}

function buildVideoSchemaId({
  _key,
  sourceType,
  videoFile,
  videoId,
}: Pick<MediaVideoProps, "_key" | "sourceType" | "videoFile" | "videoId">) {
  if (_key) {
    return _key;
  }

  if (sourceType === "file") {
    return videoFile?.asset?._ref ?? "file-video";
  }

  return `${sourceType}-${videoId ?? "unknown"}`;
}

/**
 * Editorial video. Resolves the discriminated `sourceType` to a single URL and
 * hands it to the shared player.
 *
 * Hosted embeds go through `prepareVideoURL`, which applies the cookie-consent
 * rules — the reason this cannot live in `packages/ui`.
 */
function MediaVideo({
  _key,
  aspectRatio,
  autoplay,
  className,
  description,
  durationSeconds,
  loop,
  muted,
  poster,
  regionsAllowed,
  sourceType,
  title,
  uploadDate,
  videoFile,
  videoId,
}: MediaVideoProps) {
  const cookieConsent = usePageBuilderCookieConsent();
  const sourceUrl =
    sourceType === "file"
      ? getSanityFileUrl(videoFile)
      : videoId
        ? buildHostedVideoUrl(sourceType, videoId)
        : undefined;

  const videoSrc = useMemo(
    () =>
      sourceUrl && sourceType !== "file"
        ? prepareVideoURL(sourceUrl, cookieConsent)
        : sourceUrl,
    [cookieConsent, sourceType, sourceUrl],
  );

  const videoObject = useMemo(
    () =>
      buildVideoObjectFromMediaVideo({
        _key,
        description,
        durationSeconds,
        poster,
        regionsAllowed,
        sourceType,
        title,
        uploadDate,
        videoFile,
        videoId,
      }),
    [
      _key,
      description,
      durationSeconds,
      poster,
      regionsAllowed,
      sourceType,
      title,
      uploadDate,
      videoFile,
      videoId,
    ],
  );

  if (!videoSrc) {
    return null;
  }

  const posterUrl = poster?.asset?._ref ? urlFor(poster).url() : undefined;

  const playerAspectClass =
    aspectRatio && aspectRatio !== "auto"
      ? getMediaAspectClass(aspectRatio)
      : "aspect-auto";

  const schemaId = buildVideoSchemaId({
    _key,
    sourceType,
    videoFile,
    videoId,
  });

  return (
    <>
      {videoObject ? (
        <VideoSchema schemaId={schemaId} videoData={videoObject} />
      ) : null}
      <MediaFigure className={className}>
        <VideoPlayer
          className={cn("w-full", playerAspectClass)}
          contentAssetId={videoSrc}
          controls
          light={posterUrl}
          loop={loop}
          // Browsers only start muted video automatically; the schema enforces
          // the same pairing so the two cannot disagree.
          muted={autoplay ? true : muted}
          playing={autoplay}
          src={videoSrc}
        />
      </MediaFigure>
    </>
  );
}

export { MediaVideo };
